import { useState } from 'react'
import { Filter, ListPlus, Users } from 'lucide-react'
import Modal from '@/components/ui/Modal'
import { modalButton } from '@/components/ui/modalButton'
import Select from '@/components/ui/Select'
import CriteriaEditor from '@/features/lists/CriteriaEditor'
import { cleanCriterion, criterionComplete, emptyCriterion } from '@/features/lists/criteria'
import { useCreateList, useCriteriaCount, useListFolders, useUpdateList } from '@/features/lists/useLists'
import { useMembers } from '@/features/team/useTeam'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { cn } from '@/lib/cn'
import type { ContactList, ListCriterion, ListType } from '@/api/lists'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/**
 * Create or edit a list: name, what it's for, static or dynamic, folder, owner — and for a dynamic list
 * its filters with a live count of who matches, so the result is visible before saving.
 */
export default function ListFormModal({
  list,
  defaultFolderId,
  onClose,
  onSaved,
}: {
  /** The list to edit; omit to create one. */
  list?: ContactList
  defaultFolderId?: string | null
  onClose: () => void
  onSaved?: (list: ContactList) => void
}) {
  const { data: folders } = useListFolders()
  const { data: members } = useMembers()
  const create = useCreateList()
  const update = useUpdateList()
  const save = list ? update : create

  const [name, setName] = useState(list?.name ?? '')
  const [description, setDescription] = useState(list?.description ?? '')
  const [listType, setListType] = useState<ListType>(list?.listType ?? 'manual')
  const [criteria, setCriteria] = useState<ListCriterion[]>(
    list?.criteria?.length ? list.criteria.map((c) => ({ ...c })) : [emptyCriterion()],
  )
  const [folderId, setFolderId] = useState(list?.folderId ?? defaultFolderId ?? '')
  const [ownerId, setOwnerId] = useState(list?.ownerUserId ?? '')

  const dynamic = listType === 'dynamic'
  const complete = criteria.filter(criterionComplete)
  const count = useCriteriaCount(complete.map(cleanCriterion), dynamic)
  const canSave = name.trim() !== '' && (!dynamic || criteria.every(criterionComplete))
  const losingMembers = list?.listType === 'manual' && dynamic && list.memberCount > 0

  function submit() {
    if (!canSave) return
    const input = {
      name: name.trim(),
      description: description.trim() || null,
      listType,
      criteria: dynamic ? criteria.map(cleanCriterion) : [],
      folderId: folderId || null,
      ownerUserId: ownerId || null,
    }
    const done = { onSuccess: (saved: ContactList) => { onSaved?.(saved); onClose() } }
    if (list) update.mutate({ id: list.id, input }, done)
    else create.mutate(input, done)
  }

  return (
    <Modal
      title={list ? `Edit ${list.name}` : 'New list'}
      icon={<ListPlus size={18} className="text-brand-primary" />}
      onClose={onClose}
      size="lg"
      footer={
        <>
          {save.isError && <p className="text-danger text-xs mr-auto">{apiErrorMessage(save.error)}</p>}
          <button type="button" onClick={onClose} className={modalButton.ghost}>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canSave || save.isPending} className={modalButton.primary}>
            {save.isPending ? 'Saving…' : list ? 'Save changes' : 'Create list'}
          </button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">List name</label>
          <input autoFocus className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. VIP buyers" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
          <textarea
            className={cn(inputCls, 'resize-y')}
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this list for?"
          />
        </div>

        <div>
          <span className="block text-xs font-medium text-gray-500 mb-1">Type</span>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="List type">
            {(
              [
                ['manual', 'Static', 'You add and remove contacts by hand', Users],
                ['dynamic', 'Dynamic', 'Contacts join and leave by filters', Filter],
              ] as const
            ).map(([value, label, hint, Icon]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={listType === value}
                onClick={() => setListType(value)}
                className={cn(
                  'flex items-start gap-2.5 rounded-lg border p-3 text-left',
                  listType === value ? 'border-brand-primary bg-brand-primary/5' : 'border-gray-200 hover:border-gray-300',
                )}
              >
                <Icon size={16} className={listType === value ? 'text-brand-primary mt-0.5' : 'text-gray-400 mt-0.5'} />
                <span>
                  <span className="block text-sm font-medium text-brand-darkest">{label}</span>
                  <span className="block text-xs text-gray-500">{hint}</span>
                </span>
              </button>
            ))}
          </div>
          {losingMembers && (
            <p className="text-xs text-amber-700 mt-2">
              Its {list?.memberCount} hand-picked contacts will be replaced by whoever matches the filters.
            </p>
          )}
        </div>

        {dynamic && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-gray-500">Filters — contacts matching all of them</span>
              <span className="text-xs text-gray-500" aria-live="polite">
                {count.data != null ? (
                  <>
                    <span className="font-semibold text-brand-darkest">{count.data}</span> contact
                    {count.data === 1 ? '' : 's'} match
                  </>
                ) : (
                  'Counting…'
                )}
              </span>
            </div>
            <CriteriaEditor criteria={criteria} onChange={setCriteria} />
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Folder</label>
            <Select size="md" value={folderId} onChange={(e) => setFolderId(e.target.value)} className="w-full">
              <option value="">No folder</option>
              {(folders ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Owner</label>
            <Select size="md" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className="w-full">
              {!list && <option value="">Me</option>}
              {list && !ownerId && <option value="">No owner</option>}
              {(members ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </form>
    </Modal>
  )
}
